import { useState, useEffect } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { Package, MessageSquare, Eye, ShoppingCart, TrendingUp } from "lucide-react";

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalProducts: 0,
    featuredProducts: 0,
    hiddenProducts: 0,
    totalMessages: 0,
    unreadMessages: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [productsRes, messagesRes] = await Promise.all([
          api.get("/admin/products"),
          api.get("/admin/messages")
        ]);

        const products = productsRes.data;
        const messages = messagesRes.data;

        setStats({
          totalProducts: products.length,
          featuredProducts: products.filter(p => p.is_featured).length,
          hiddenProducts: products.filter(p => !p.is_visible).length,
          totalMessages: messages.length,
          unreadMessages: messages.filter(m => !m.is_read).length
        });
      } catch (error) {
        console.error("Error fetching stats:", error);
      }
      setLoading(false);
    };

    fetchStats();
  }, []);

  const statCards = [
    { icon: Package, label: "Total Products", value: stats.totalProducts, color: "primary" },
    { icon: TrendingUp, label: "Featured", value: stats.featuredProducts, color: "accent" },
    { icon: Eye, label: "Hidden", value: stats.hiddenProducts, color: "muted" },
    { icon: MessageSquare, label: "Messages", value: stats.totalMessages, color: "primary" },
  ];

  return (
    <AdminLayout title="Dashboard">
      <div className="space-y-8">
        {/* Welcome */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-slate-900/50 border border-slate-800 rounded-xl p-6"
        >
          <h2 className="text-xl font-bold tracking-wide mb-2">Welcome, Admin!</h2>
          <p className="text-muted-foreground">
            Welcome to your AeroCore Scripts management panel. You can manage your products and site settings here.
          </p>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 hover:border-primary/30 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">
                    {stat.label}
                  </p>
                  <p className="text-3xl font-bold tracking-tight">
                    {loading ? "-" : stat.value}
                  </p>
                </div>
                <div className={`w-10 h-10 bg-${stat.color}/10 border border-${stat.color}/30 rounded-lg flex items-center justify-center`}>
                  <stat.icon className={`w-5 h-5 text-${stat.color}`} />
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h3 className="text-sm uppercase tracking-widest text-muted-foreground mb-4">
            Quick Actions
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <a
              href="/admin/products/new"
              className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 hover:border-primary/30 transition-colors group"
              data-testid="quick-add-product"
            >
              <Package className="w-8 h-8 text-primary mb-3" />
              <h4 className="font-bold tracking-wide mb-1 group-hover:text-primary transition-colors">
                Add New Product
              </h4>
              <p className="text-sm text-muted-foreground">
                Add a new script to your store
              </p>
            </a>
            <a
              href="/admin/messages"
              className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 hover:border-primary/30 transition-colors group"
              data-testid="quick-messages"
            >
              <MessageSquare className="w-8 h-8 text-primary mb-3" />
              <h4 className="font-bold tracking-wide mb-1 group-hover:text-primary transition-colors">
                View Messages
                {stats.unreadMessages > 0 && (
                  <span className="ml-2 px-2 py-0.5 bg-destructive text-xs rounded-full">
                    {stats.unreadMessages} new
                  </span>
                )}
              </h4>
              <p className="text-sm text-muted-foreground">
                Check customer messages
              </p>
            </a>
            <a
              href="/admin/settings"
              className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 hover:border-primary/30 transition-colors group"
              data-testid="quick-settings"
            >
              <ShoppingCart className="w-8 h-8 text-primary mb-3" />
              <h4 className="font-bold tracking-wide mb-1 group-hover:text-primary transition-colors">
                Site Settings
              </h4>
              <p className="text-sm text-muted-foreground">
                Edit site content
              </p>
            </a>
          </div>
        </motion.div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
